import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { EmptyState } from "./EmptyState";
import { InlineError, Spinner } from "./Feedback";

export interface CommandPaletteNavigateItem {
  id: string;
  label: string;
  onSelect: () => void;
}

export interface CommandPaletteAgentItem {
  displayName: string;
  id: string;
  onSelect: () => void;
  shortId: string;
}

export interface CommandPaletteProps {
  agentItems: CommandPaletteAgentItem[];
  hasNextAgentPage: boolean;
  isAgentsError: boolean;
  isAgentsPending: boolean;
  navigateItems: CommandPaletteNavigateItem[];
  onClose: () => void;
  searchIcon?: ReactNode;
}

type PaletteEntry =
  | {
      item: CommandPaletteNavigateItem;
      kind: "navigate";
      label: string;
    }
  | {
      item: CommandPaletteAgentItem;
      kind: "agent";
      label: string;
    };

// Shared with `ScopePicker` so the app has ONE fuzzy-match rule for list
// filtering.
export function matchesSubsequence(query: string, value: string): boolean {
  const needle = query.trim().toLocaleLowerCase();
  if (needle.length === 0) {
    return true;
  }

  let cursor = 0;
  for (const character of value.toLocaleLowerCase()) {
    const target = needle[cursor];
    if (target !== undefined && character === target) {
      cursor += 1;
    }
    if (cursor === needle.length) {
      return true;
    }
  }

  return false;
}

export function CommandPalette({
  agentItems,
  hasNextAgentPage,
  isAgentsError,
  isAgentsPending,
  navigateItems,
  onClose,
  searchIcon,
}: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [previousQuery, setPreviousQuery] = useState(query);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const trimmedQuery = query.trim();

  const navigateMatches = useMemo(
    () => navigateItems.filter((item) => matchesSubsequence(query, item.label)),
    [navigateItems, query],
  );
  const agentMatches = useMemo(
    () =>
      agentItems.filter((item) => matchesSubsequence(query, item.displayName)),
    [agentItems, query],
  );

  const entries: PaletteEntry[] = useMemo(
    () => [
      ...navigateMatches.map((item) => ({
        item,
        kind: "navigate" as const,
        label: item.label,
      })),
      ...agentMatches.map((item) => ({
        item,
        kind: "agent" as const,
        label: item.displayName,
      })),
    ],
    [agentMatches, navigateMatches],
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  if (previousQuery !== query) {
    setPreviousQuery(query);
    setActiveIndex(0);
  } else if (activeIndex >= entries.length && entries.length > 0) {
    setActiveIndex(entries.length - 1);
  }

  const activeDescendant =
    entries.length > 0
      ? `${listboxId}-option-${String(activeIndex)}`
      : undefined;
  const showAgentState = isAgentsPending || isAgentsError;
  const showAgentGroup = showAgentState || agentMatches.length > 0;
  const showEmpty =
    trimmedQuery.length > 0 &&
    navigateMatches.length === 0 &&
    !showAgentState &&
    agentMatches.length === 0;

  const selectEntry = (entry: PaletteEntry) => {
    entry.item.onSelect();
    onClose();
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      return;
    }

    if (entries.length === 0) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % entries.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(
        (current) => (current - 1 + entries.length) % entries.length,
      );
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const activeEntry = entries[activeIndex];
      if (activeEntry) {
        selectEntry(activeEntry);
      }
    }
  };

  const renderOption = (entry: PaletteEntry, index: number) => {
    const isActive = index === activeIndex;
    const optionId = `${listboxId}-option-${String(index)}`;

    return (
      <button
        aria-selected={isActive}
        className={[
          "marker-command-palette__row",
          isActive ? "marker-command-palette__row--active" : "",
        ].join(" ")}
        id={optionId}
        key={`${entry.kind}-${entry.item.id}`}
        onClick={() => {
          selectEntry(entry);
        }}
        onMouseEnter={() => {
          setActiveIndex(index);
        }}
        role="option"
        tabIndex={-1}
        type="button"
      >
        {entry.kind === "navigate" ? (
          <>
            <span className="marker-command-palette__row-label">
              {entry.item.label}
            </span>
            <span className="marker-command-palette__row-meta">Navigate</span>
          </>
        ) : (
          <>
            <span className="marker-command-palette__row-label">
              {entry.item.displayName}
            </span>
            <span className="marker-command-palette__row-id">
              {entry.item.shortId}
            </span>
            <span className="marker-command-palette__row-meta">
              Transcripts
            </span>
          </>
        )}
      </button>
    );
  };

  return (
    <div className="marker-command-palette" role="presentation">
      <button
        aria-label="Close command palette"
        className="marker-command-palette__scrim"
        onClick={onClose}
        type="button"
      />
      <section
        aria-label="Command palette"
        aria-modal="true"
        className="marker-command-palette__panel"
        role="dialog"
      >
        <div className="marker-command-palette__input">
          {searchIcon}
          <input
            ref={inputRef}
            aria-activedescendant={activeDescendant}
            aria-controls={listboxId}
            aria-expanded="true"
            aria-label="Command palette search"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            onKeyDown={onInputKeyDown}
            placeholder="Jump to…"
            role="combobox"
            type="text"
            value={query}
          />
          <kbd>Esc</kbd>
        </div>
        <div className="marker-command-palette__body">
          <div
            aria-label="Command palette results"
            className="marker-command-palette__listbox"
            id={listboxId}
            role="listbox"
          >
            {navigateMatches.length > 0 ? (
              <div className="marker-command-palette__group">
                <div className="marker-command-palette__group-label">
                  Navigate
                </div>
                {navigateMatches.map((item, index) =>
                  renderOption(
                    {
                      item,
                      kind: "navigate",
                      label: item.label,
                    },
                    index,
                  ),
                )}
              </div>
            ) : null}

            {showAgentGroup ? (
              <div className="marker-command-palette__group">
                <div className="marker-command-palette__group-label">
                  Agents
                </div>
                {isAgentsPending ? (
                  <div className="marker-command-palette__state">
                    <Spinner label="Loading agents" />
                    <span>Loading agents…</span>
                  </div>
                ) : null}
                {isAgentsError ? (
                  <InlineError className="marker-command-palette__error">
                    Couldn&apos;t load agents
                  </InlineError>
                ) : null}
                {!showAgentState
                  ? agentMatches.map((item, index) =>
                      renderOption(
                        {
                          item,
                          kind: "agent",
                          label: item.displayName,
                        },
                        navigateMatches.length + index,
                      ),
                    )
                  : null}
              </div>
            ) : null}

            {showEmpty ? <EmptyState title="No matches" /> : null}
          </div>
          <p className="marker-command-palette__footer">
            Matching {agentItems.length} loaded agents
            {/* OPEN(OQ-EXT-48): no server-side search endpoint exists; this
                footer is the v1 seam for the future search contract. */}
            {hasNextAgentPage ? " · more exist" : ""}
          </p>
        </div>
      </section>
    </div>
  );
}
